import { Check, Pause, Play } from "lucide-react";
import * as React from "react";
import styled from "styled-components";
import { KeepAwake } from "@capacitor-community/keep-awake";
import { LocalNotifications } from "@capacitor/local-notifications";

interface Props {
  duration: string;
  isFocused: boolean;
}

type State = "paused" | "running" | "done";

export function Timer({ duration, isFocused }: Props) {
  const [state, setState] = React.useState<State>("paused");
  const [elapsed, setElapsed] = React.useState(0);
  const durationSecs = getDurationSecs(duration);

  React.useEffect(() => {
    LocalNotifications.requestPermissions();
  }, []);

  React.useEffect(() => {
    if (!isFocused && state === "running") {
      setState("paused");
    }
  }, [isFocused]);

  React.useEffect(() => {
    switch (state) {
      case "paused":
      case "done":
        KeepAwake.allowSleep();
        return;
      case "running":
        KeepAwake.keepAwake();
        return () => {
          KeepAwake.allowSleep();
        };
    }
  }, [state]);

  React.useEffect(() => {
    if (state === "running") {
      let lastTime = 0;
      let cancelled = false;
      const handler = (time: number) => {
        if (cancelled) {
          return;
        }
        if (!lastTime) {
          lastTime = time;
        }
        const phaseElapsed = (time - lastTime) / 1000;
        setElapsed((e) => e + phaseElapsed);
        lastTime = time;
        setTimeout(() => requestAnimationFrame(handler), 50);
      };

      requestAnimationFrame(handler);
      return () => {
        cancelled = true;
      };
    }
    return () => {};
  }, [state]);

  React.useEffect(() => {
    if (elapsed >= durationSecs) {
      setState("done");
      LocalNotifications.schedule({
        notifications: [
          {
            title: "Timer expired",
            body: "Timer has finished",
            id: (Math.random() * 4294967296) >>> 0,
            sound: "strike.mp3",
          },
        ],
      });
    }
  }, [elapsed]);

  const togglePause = () => {
    switch (state) {
      case "running":
        return setState("paused");
      case "paused":
        return setState("running");
    }
  };

  return (
    <Container progress={elapsed / durationSecs} onClick={togglePause}>
      <Time>{formatTime(elapsed)}</Time>
      <Controls>
        {(() => {
          switch (state) {
            case "paused":
              return (
                <Button>
                  <Play color="#999" fill="#999" strokeWidth={1} size={20} />
                </Button>
              );
            case "running":
              return (
                <Button>
                  <Pause color="#999" fill="#999" strokeWidth={1} size={20} />
                </Button>
              );
            case "done":
              return (
                <Button>
                  <Check color="green" strokeWidth={2} size={20} />
                </Button>
              );
          }
        })()}
      </Controls>
    </Container>
  );
}

function formatTime(t: number) {
  const hours = Math.floor(t / 3600);
  const minutes = Math.floor(t / 60)
    .toString()
    .padStart(2, "0");
  const seconds = Math.floor(t % 60)
    .toString()
    .padStart(2, "0");
  if (hours) {
    return `${hours}:${minutes}:${seconds}`;
  }
  return `${minutes}:${seconds}`;
}

function getDurationSecs(duration: string) {
  const [seconds, minutes, hours = 0] = duration
    .split(":")
    .reverse()
    .map((s) => parseInt(s, 10));
  return seconds + minutes * 60 + hours * 3600;
}

const Container = styled.div<{ progress: number }>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  background: var(--box-color);
  padding: 8px 8px 5px;
  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    height: 3px;
    width: 100%;
    background: ${({ progress: p }) => `
    linear-gradient(
      to right,
      green ${p * 100}%, var(--box-color-contrast) ${p * 100}% 100%
    );`};
  }
`;

const Time = styled.div`
  font-size: 16px;
  flex-grow: 1;
`;

const Controls = styled.div``;

const Button = styled.div``;
